/**
 * Pluckonomics — P&L statement (Phase E.3).
 *
 * Reads `GET /api/admin/analytics/pnl`. Renders a balance-sheet style
 * table with: This month | Last month | Δ%.
 *
 * Display rules:
 *   - `kind: revenue`    : top of table, plus sign positive amounts
 *   - `kind: deduction`  : prefixed with "−" sign, intermediate rows
 *   - `kind: total`      : bold, top-border, larger font; "Net platform take"
 *   - Gated rows render "—" with the orange pill in the value cells
 *     and the note appears under the label.
 */
import { Card, CardContent } from "@/components/ui/card";

function _formatCents(cents) {
  if (cents === null || cents === undefined) return "—";
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return sign + new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(abs / 100);
}

function _formatPct(p) {
  if (p === null || p === undefined) return "—";
  const sign = p > 0 ? "+" : "";
  return `${sign}${p.toFixed(1)}%`;
}

function GatedPill({ text }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: "rgba(248, 150, 35, 0.12)", color: "var(--pp-orange)" }}
    >
      {text}
    </span>
  );
}

export default function PnLStatement({ data }) {
  if (!data) {
    return (
      <div className="p-6 text-sm" style={{ color: "var(--pp-text-soft)" }}
           data-testid="pluckonomics-pnl-loading">
        Loading P&L…
      </div>
    );
  }
  const { lines, this_month_label, last_month_label } = data;
  return (
    <Card
      style={{ background: "var(--pp-v3-surface)", borderColor: "var(--pp-border)" }}
      data-testid="pluckonomics-pnl-card"
    >
      <CardContent className="p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold" style={{ color: "var(--pp-text)" }}
              data-testid="pluckonomics-pnl-heading">
            P&amp;L statement
          </h2>
          <p className="mt-1 text-xs" style={{ color: "var(--pp-text-soft)" }}>
            Month-over-month revenue, deductions, and net platform take.
          </p>
        </div>

        <div className="overflow-x-auto" data-testid="pluckonomics-pnl-table-wrap">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--pp-border)" }}>
                <th className="py-2 pr-3 text-left text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)" }}>Line</th>
                <th className="py-2 px-3 text-right text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)" }}
                    data-testid="pluckonomics-pnl-col-this-month">
                  {this_month_label}
                </th>
                <th className="py-2 px-3 text-right text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)" }}
                    data-testid="pluckonomics-pnl-col-last-month">
                  {last_month_label}
                </th>
                <th className="py-2 pl-3 text-right text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)" }}>Δ%</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => {
                const isTotal = line.kind === "total";
                const isGated = line.display_state === "stripe_gated";
                const labelColor = "var(--pp-text)";
                const valueColor = isGated
                  ? "var(--pp-text-soft)"
                  : (isTotal && line.this_month_cents != null && line.this_month_cents < 0
                       ? "var(--pp-orange)"
                       : "var(--pp-text)");
                const deltaColor = (() => {
                  if (line.delta_pct === null || line.delta_pct === undefined) return "var(--pp-text-soft)";
                  // For deductions: bigger deduction (positive delta) is BAD = orange.
                  // For revenue/total: positive delta is GOOD = neutral text.
                  if (line.kind === "deduction") {
                    return line.delta_pct > 0 ? "var(--pp-orange)" : "var(--pp-text)";
                  }
                  return line.delta_pct < 0 ? "var(--pp-orange)" : "var(--pp-text)";
                })();
                // Display rules:
                //   - deduction → always show with "−" prefix on absolute
                //     value (UI semantics: subtractions from revenue).
                //   - revenue / total → show signed value as-is (totals
                //     can legitimately be negative when comp exceeds
                //     commission; the orange color carries the warning).
                const renderCents = (cents) => {
                  if (cents === null || cents === undefined) return "—";
                  if (line.kind === "deduction") {
                    return `−${_formatCents(Math.abs(cents))}`;
                  }
                  return _formatCents(cents);
                };
                return (
                  <tr
                    key={`${line.label}-${idx}`}
                    style={{
                      borderBottom: "1px solid var(--pp-border)",
                      borderTop: isTotal ? "2px solid var(--pp-border)" : undefined,
                    }}
                    data-testid={`pluckonomics-pnl-row-${idx}`}
                    data-line-kind={line.kind}
                    data-display-state={line.display_state}
                  >
                    <td className="py-3 pr-3">
                      <div className={isTotal ? "font-semibold" : "font-medium"}
                           style={{ color: labelColor }}>
                        {line.label}
                      </div>
                      {isGated && line.note && (
                        <div className="mt-1">
                          <GatedPill text={line.note} />
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right tabular-nums"
                        style={{
                          color: valueColor,
                          fontWeight: isTotal ? 700 : 500,
                          fontSize: isTotal ? "1rem" : undefined,
                        }}>
                      {isGated ? "—" : renderCents(line.this_month_cents)}
                    </td>
                    <td className="py-3 px-3 text-right tabular-nums"
                        style={{ color: valueColor, fontWeight: isTotal ? 700 : 500 }}>
                      {isGated ? "—" : renderCents(line.last_month_cents)}
                    </td>
                    <td className="py-3 pl-3 text-right tabular-nums"
                        style={{ color: deltaColor, fontWeight: 500 }}>
                      {_formatPct(line.delta_pct)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
